import { useState, useEffect, useMemo, useRef } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { AppList, apps } from "@/components/AppList";
import { CATEGORY_LABELS as categoryLabels } from "@/utils/constants";
import { UserProfileCard } from "@/components/UserProfileCard";
import { useAppSelector } from "@/store/hooks";
import { UserProfile, Session } from "@/types/profile";
import { AccessDenialDialog } from "@/components/AccessDenialDialog";
import { RedirectLoopDialog } from "@/components/RedirectLoopDialog";
import { ManageAccess } from "@/components/ManageAccess";
import { checkRedirectPermissions } from "@/utils/redirectPermissionCheck";
import { clearRedirectLoopFor, LOOP_REDIRECT_QUERY_PARAM } from "@/utils/redirectLoopGuard";
import { AppConfig } from "@/types/app";
import {
    LayoutGrid,
    Wand2,
    ChartPie,
    Shield,
    Globe,
    ChevronRight,
    MonitorPlay,
    type LucideIcon
} from "lucide-react";

interface PageLayoutContext {
    profile: UserProfile | null;
    sessions: Session[];
    profileImage: string;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
    content: MonitorPlay,
    tools: Wand2,
    analytics: ChartPie,
    admin: Shield,
    other: Globe
};

const Home = () => {
    const { userName, userEmail } = useAppSelector((state) => state.auth);
    const location = useLocation();
    const navigate = useNavigate();

    const context = useOutletContext<PageLayoutContext>();
    const profile = context?.profile ?? null;
    const profileImage = context?.profileImage ?? "";
    const sessions = context?.sessions ?? [];
    
    const [activeSection, setActiveSection] = useState("");
    const [showAccessDenialDialog, setShowAccessDenialDialog] = useState(false);
    const [deniedAppConfig, setDeniedAppConfig] = useState<AppConfig | null>(null);
    const [deniedRedirectUrl, setDeniedRedirectUrl] = useState<string | null>(null);
    const [showManageAccessDialog, setShowManageAccessDialog] = useState(false);
    const [showRedirectLoopDialog, setShowRedirectLoopDialog] = useState(false);
    const [loopTargetOrigin, setLoopTargetOrigin] = useState<string | null>(null);
    const hasCheckedRedirect = useRef(false);
    const hasCheckedLoopRedirect = useRef(false);

    const navItems = useMemo(() => {
        const categories = new Set<string>();
        apps.filter(app => !app.hidden).forEach(app => categories.add(app.category));

        return Array.from(categories).map(cat => ({
            id: cat,
            label: categoryLabels[cat as keyof typeof categoryLabels],
            icon: CATEGORY_ICONS[cat] || LayoutGrid,
        }));
    }, []);

    useEffect(() => {
        if (navItems.length > 0 && !activeSection) {
            setActiveSection(navItems[0].id);
        }
    }, [navItems, activeSection]);

    // Check for redirect parameter and show access denial popup if needed
    useEffect(() => {
        if (hasCheckedRedirect.current) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const redirectParam = params.get("redirect");

        if (redirectParam && profile?.permissions) {
            hasCheckedRedirect.current = true;

            const { hasAccess, appConfig } = checkRedirectPermissions(
                redirectParam,
                profile.permissions
            );

            if (!hasAccess) {
                setDeniedAppConfig(appConfig);
                setDeniedRedirectUrl(redirectParam);
                setShowAccessDenialDialog(true);

                const newParams = new URLSearchParams(location.search);
                newParams.delete("redirect");
                const newSearch = newParams.toString();
                const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
                navigate(newUrl, { replace: true });
            }
        }
    }, [location.search, profile?.permissions, navigate, location.pathname]);

    const handleCloseAccessDenialDialog = () => {
        setShowAccessDenialDialog(false);
        setDeniedAppConfig(null);
        setDeniedRedirectUrl(null);
    };

    // Check for redirect-loop param and show the "Something went wrong" dialog
    useEffect(() => {
        if (hasCheckedLoopRedirect.current) return;

        const params = new URLSearchParams(location.search);
        const loopOrigin = params.get(LOOP_REDIRECT_QUERY_PARAM);
        if (!loopOrigin) return;

        hasCheckedLoopRedirect.current = true;
        setLoopTargetOrigin(loopOrigin);
        setShowRedirectLoopDialog(true);

        const newParams = new URLSearchParams(location.search);
        newParams.delete(LOOP_REDIRECT_QUERY_PARAM);
        const newSearch = newParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        navigate(newUrl, { replace: true });
    }, [location.search, location.pathname, navigate]);

    const handleCloseRedirectLoopDialog = () => {
        setShowRedirectLoopDialog(false);
        if (loopTargetOrigin) {
            clearRedirectLoopFor(loopTargetOrigin);
        }
        setLoopTargetOrigin(null);
    };

    const handleRequestAccess = () => {
        setShowAccessDenialDialog(false);
        setShowManageAccessDialog(true);
    };

    useEffect(() => {
        if (navItems.length === 0) return;

        const handleScroll = () => {
            const sections = navItems.map(item => item.id);
            const scrollPosition = window.scrollY + 200;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        const timeoutId = setTimeout(() => {
            window.addEventListener("scroll", handleScroll, { passive: true });
            handleScroll();
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [navItems]);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementPosition - offset,
                behavior: "smooth"
            });
        }
    };

    if (!apps || apps.length === 0) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <p className="text-muted-foreground">No apps available</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <AccessDenialDialog
                isOpen={showAccessDenialDialog}
                onClose={handleCloseAccessDenialDialog}
                onRequestAccess={handleRequestAccess}
                appConfig={deniedAppConfig}
                redirectUrl={deniedRedirectUrl || undefined}
            />
            <RedirectLoopDialog
                isOpen={showRedirectLoopDialog}
                onClose={handleCloseRedirectLoopDialog}
                targetOrigin={loopTargetOrigin}
            />
            <div className="min-h-screen bg-background">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                    <aside className="hidden lg:block w-72 shrink-0">
                        <div className="sticky top-20">
                            <UserProfileCard
                                profile={profile}
                                userName={userName || null}
                                userEmail={userEmail || null}
                                profileImage={profileImage}
                                currentSession={sessions.find(s => s.is_current)}
                            />
                            <nav className="space-y-1">
                                {navItems.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = activeSection === item.id;

                                    return (
                                        <button
                                            key={item.id}
                                            onClick={() => scrollToSection(item.id)}
                                            className={`
                                                w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left
                                                transition-all duration-300 ease-out group relative
                                                ${isActive
                                                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                                                    : "hover:bg-muted/80 text-muted-foreground hover:text-foreground"
                                                }
                                            `}
                                        >
                                            <div className={`
                                                absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-full transition-all duration-300
                                                ${isActive ? "h-8 bg-primary-foreground/50" : "h-0"}
                                            `} />

                                            <div className={`
                                                p-2 rounded-lg transition-colors duration-200
                                                ${isActive ? "bg-primary-foreground/20" : "bg-muted group-hover:bg-background"}
                                            `}>
                                                <Icon className="w-4 h-4" />
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm">{item.label}</p>
                                            </div>

                                            <ChevronRight className={`
                                                w-4 h-4 transition-all duration-200
                                                ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0"}
                                            `} />
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    <div className="lg:hidden flex gap-2 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = activeSection === item.id;

                            return (
                                <button
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    className={`
                                flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap
                                transition-all duration-200 shrink-0
                                ${isActive
                                            ? "bg-primary text-primary-foreground shadow-md"
                                            : "bg-muted/50 text-muted-foreground hover:bg-muted"
                                        }
                            `}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span className="text-sm font-medium">{item.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    <main className="flex-1 min-w-0 space-y-8">
                        <AppList permissions={profile?.permissions || []} />
                    </main>
                    </div>
                </div>
            </div>

            {/* Hidden ManageAccess component for showing Add Access dialog */}
            {showManageAccessDialog && profile && (
                <div style={{ display: 'none' }}>
                    <ManageAccess
                        permissions={profile.permissions || []}
                        decoded_permissions={profile.decoded_permissions}
                        user_req={profile.user_req}
                        isProfile={true}
                        userId={profile.id || null}
                        autoOpenDialog={true}
                    />
                </div>
            )}
        </>
    );
};

export default Home;
